import { Button } from 'antd';
import { CalendarClock, ClipboardCopy, ShieldAlert } from 'lucide-react';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { RowListSkeleton } from '../../../../../shared/components/RowListSkeleton';
import { useOrganization } from '../../../../../shared/features/organization/components/OrganizationProvider';
import { useCopyStatusSummary } from '../hooks/use-copy-status-summary';
import { useTripTimeline } from '../hooks/use-trip-timeline';
import { durationText } from '../../../../../shared/policies/duration-text';
import { referenceTime } from '../policies/timeline-reference.policy';
import { statusSummary } from '../policies/timeline-summary.policy';
import { clockWithDay } from '../policies/timeline-time.policy';
import type { TripTimeline } from '../trip-timeline.api';
import { TimelineActualRow } from './TimelineActualRow';
import { TimelineStopBlock } from './TimelineStopBlock';

interface TripTimelinePanelProps {
  tripId: string;
  tripNumber: string;
  /** Changes when the trip may have moved on; the timeline is read again without going back to the skeleton. */
  refreshKey?: number;
}

function planLine(timeline: TripTimeline, timeText: (iso: string) => string): string {
  const plan = timeline.plan;
  if (!plan) return '';
  const driving =
    plan.drivingSeconds !== null ? `Routed driving ${durationText(plan.drivingSeconds)}` : 'Not every leg is routed, so later stops have no planned time';
  if (!plan.startAt) return `${driving}. No planned times yet: the trip has no window and has not been dispatched.`;
  return `${driving}. Planned from ${timeText(plan.startAt)} (${plan.startedFrom === 'window' ? 'the assigned window' : 'dispatch'}).`;
}

function OtherEvents({ timeline, timezone, timeText }: { timeline: TripTimeline; timezone: string; timeText: (iso: string) => string }) {
  if (timeline.unplaced.length === 0) return null;
  return (
    <section aria-label="Other events" className="rounded-lg border border-line bg-surface p-4">
      <h3 className="mb-2 text-sm font-semibold text-fg">Other events</h3>
      <ul className="flex flex-col gap-2">
        {timeline.unplaced.map((actual) => (
          <TimelineActualRow key={actual.sources[0].eventId} actual={actual} timezone={timezone} timeText={timeText} />
        ))}
      </ul>
    </section>
  );
}

/** A trip's actuals against its plan, stop by stop, with a status summary to paste into a message to the customer (gitaumoses4/chukua#90). */
export function TripTimelinePanel({ tripId, tripNumber, refreshKey }: TripTimelinePanelProps) {
  const state = useTripTimeline(tripId, refreshKey);
  const { timezone } = useOrganization();
  const { copy, error } = useCopyStatusSummary();

  if (state.status === 'loading') return <RowListSkeleton label="Loading timeline" rows={3} />;
  if (state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load the timeline" description="Something went wrong. Try reloading the page." />;
  }

  const { timeline } = state;
  const reference = referenceTime(timeline);
  const timeText = (iso: string) => (reference ? clockWithDay(iso, reference, timezone) : iso);
  const otherEvents = <OtherEvents timeline={timeline} timezone={timezone} timeText={timeText} />;

  if (!timeline.plan) {
    return (
      <>
        <EmptyState icon={CalendarClock} title="No plan for this trip" description="Without a plan there are no stops to set what happened against." />
        {otherEvents}
      </>
    );
  }

  const summary = statusSummary({ tripNumber, timeline, timezone });
  return (
    <section aria-label="Trip timeline" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-fg">Timeline</h2>
          <p className="text-sm text-muted">{planLine(timeline, timeText)}</p>
        </div>
        <Button icon={<ClipboardCopy className="size-4" aria-hidden />} onClick={() => void copy(summary)}>
          Copy status summary
        </Button>
      </div>
      {timeline.stops.map((stop) => (
        <TimelineStopBlock key={stop.position} stop={stop} timezone={timezone} timeText={timeText} />
      ))}
      {otherEvents}
      <section aria-label="Status summary" className="rounded-lg border border-line bg-surface p-4">
        <h3 className="mb-2 text-sm font-semibold text-fg">Status summary</h3>
        <p className="text-sm whitespace-pre-line text-fg">{summary}</p>
        {error && <p className="mt-2 text-sm text-error">{error}</p>}
      </section>
    </section>
  );
}
